import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Actions from 'actions';
import Selectors from 'selectors';
import { Loading, Card, Button, ErrorContainer, Footer } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { TextInput, SelectInput, SingleCheckboxInput } from 'components/Form';
import { arrayToOptions } from 'utils/helper';
import isEmpty from 'lodash/isEmpty';
import { iconNext } from 'assets';
import AssignmentIcon from '@material-ui/icons/Assignment';

const Onboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(Actions.fetchProfile());
    dispatch(Actions.fetchLanguages());
  }, [dispatch]);

  const isLoading = useSelector(
    Selectors.createLoadingSelector([
      Actions.FETCH_PROFILE_REQUEST,
      Actions.UPDATE_USER_REQUEST,
      Actions.FETCH_LANGUAGES_REQUEST,
    ])
  );

  const userProfile = useSelector(Selectors.userProfile);
  const languagesOptions = useSelector(Selectors.languages);
  const [error, setError] = useState(null);

  const {
    biography,
    languages,
    rep_artists: repArtist,
    rep_events: repEvents,
    rep_venues: repVenues,
    users_services: userServices,
    chat_notification: chatNotification,
  } = userProfile || {};

  const arrayStringInitValue = (array) => array?.join(', ') || '';
  const arrayInitialValues = (array) => {
    if (isEmpty(array)) return [{ name: '', description: '' }];
    if (typeof array[0] === 'string') return [{ name: '', description: '' }];
    return array;
  };

  const initialValues = {
    users_services: arrayInitialValues(userServices),
    biography: biography || '',
    languages:
      languages?.map(({ name }) => ({
        label: name,
        value: name,
      })) || [],
    rep_artists: arrayStringInitValue(repArtist),
    rep_events: arrayStringInitValue(repEvents),
    rep_venues: arrayStringInitValue(repVenues),
    chat_notification: chatNotification || false,
  };

  const validationSchema = Yup.object().shape({
    users_services: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .required('Service name is required')
          .max(100, 'Maximum 100 characters'),
        description: Yup.string()
          .required('Service description is required')
          .max(500, 'Maximum 500 characters'),
      })
    ),
    biography: Yup.string()
      .required('Biography is required')
      .max(500, 'Maximum 500 characters'),
    languages: Yup.array()
      .min(1, 'Language is required')
      .required('Language is required'),
    rep_artists: Yup.string().required('Artists represented is required'),
    rep_events: Yup.string().required('Events represented is required'),
    rep_venues: Yup.string().required('Venue represented is required'),
  });

  function handleOnSubmit(formValues, actions) {
    const {
      languages,
      rep_artists: repArtists,
      rep_events: repEvents,
      rep_venues: repVenues,
      users_services: userServices,
    } = formValues;

    const parsedFormValue = {
      ...formValues,
      languages: languages.map((item) => item.value),
      rep_artists: repArtists ? repArtists.split(', ') : [],
      rep_events: repEvents ? repEvents.split(', ') : [],
      rep_venues: repVenues ? repVenues.split(', ') : [],
      users_services_attributes: userServices,
      completed_profile: true,
    };

    dispatch(
      Actions.updateUser(parsedFormValue, {
        success: () => {
          actions.setSubmitting(false);
          dispatch(
            Actions.showDialog({
              title: 'Success',
              isSuccess: true,
              hideButtons: true,
              content: (
                <div className="flex flex-col items-center">
                  <div className="mb-6">Account setup successful!</div>
                  <div className="flex flex-col w-full space-y-4">
                    <Button
                      label="Return to Dashboard"
                      className="w-full h-12 px-4"
                      onClick={() => {
                        dispatch(Actions.hideDialog());
                        navigate('/dashboard', { replace: true });
                      }}
                      isPill
                    />
                    <Button
                      label="Update Information"
                      className="w-full h-12 px-4"
                      theme="plain"
                      outline
                      onClick={() => {
                        dispatch(Actions.hideDialog());
                        navigate('/profile', { replace: true });
                      }}
                      isPill
                    />
                  </div>
                </div>
              ),
            })
          );
        },
        failure: (error) => {
          const errorMsg = error?.response?.data?.error;
          setError(errorMsg ? errorMsg[0] : 'Failed to setup account');
          actions.setSubmitting(false);
        },
      })
    );
  }

  return (
    <div className="bg-alabaster min-h-screen py-16">
      {isLoading && isEmpty(userProfile) && (
        <Loading className="fixed left-0 top-0 bottom-0 right-0 z-50" />
      )}
      <div className="container mx-auto px-6 max-w-3xl">
        <Card className="border-t-8 border-easter p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-teal-100 rounded-full p-4 mb-4 text-easter">
              <AssignmentIcon fontSize="large" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Setup your account</h2>
            <p className="text-dusty text-center">
              Tell us about your services and experience so people can discover and connect with you.
            </p>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleOnSubmit}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="flex flex-col space-y-6">
                  {/* Services */}
                  <div>
                    <Field
                      name="users_services[0].name"
                      component={TextInput}
                      label="Service name"
                      placeholder="eg: Record music"
                      showRemaining
                      maxLength={100}
                    />
                    <Field
                      name="users_services[0].description"
                      component={TextInput}
                      label="Description"
                      placeholder="eg: Record and customize your audio experience"
                      showRemaining
                      maxLength={500}
                    />
                  </div>

                  {/* Biography */}
                  <Field
                    name="biography"
                    component={TextInput}
                    placeholder="Tell us a bit about yourself, your background, and what you do."
                    label="Add a short biography"
                    type="textarea"
                    showRemaining
                    maxLength={500}
                  />

                  {/* Languages Spoken */}
                  <Field
                    name="languages"
                    component={SelectInput}
                    isLoading={isLoading}
                    isMulti
                    placeholder="Languages Spoken"
                    options={arrayToOptions(languagesOptions)}
                    creatable
                  />

                  {/* Represented */}
                  <Field
                    name="rep_artists"
                    component={TextInput}
                    placeholder="Artists represented"
                  />
                  <Field
                    name="rep_events"
                    component={TextInput}
                    placeholder="Events represented"
                  />
                  <Field
                    name="rep_venues"
                    component={TextInput}
                    placeholder="Venue represented"
                  />

                  {/* Chat Notification */}
                  <Field
                    name="chat_notification"
                    component={SingleCheckboxInput}
                    className="mt-2"
                    label={
                      <div className="max-w-sm">
                        Email notifications for new chat messages
                      </div>
                    }
                  />
                </div>

                <ErrorContainer error={error} />

                <Footer>
                  <div className="w-full text-right">
                    <Button
                    type="submit"
                    className="text-xs md:text-base"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    label={
                      <div className="flex items-center">
                        CONTINUE
                        <img
                          alt="next"
                          src={iconNext.default}
                          className="h-4 mt-1 ml-4"
                        />
                      </div>
                    }
                  />
                  </div>
                </Footer>
              </Form>
            )}
          </Formik>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
